import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "CasaDiVetroParams";

const defaultFilters = Object.freeze({
  tipologia_atto: "",
  direzione_generale: "",
  oggetto: "",
  num_atto_da: null,
  num_atto_a: null,
  data_da: "",
  data_a: "",
});

const defaultPagination = Object.freeze({
  page: 0,
  size: 10,
  sort: "",
});

const CasaDiVetroContext = createContext();

//* -------------------- Hook per leggere i parametri URL --------------------
const useUrlParams = () => {
  const { search } = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(search);
    return {
      uuid: params.get("uuid"),
    };
  }, [search]);
};

//* -------------------- Hook personalizzato per localStorage --------------------
const usePersistedState = (key, defaultValue, skipSave = false) => {
  const [state, setState] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultValue, ...parsed };
      }
    } catch (error) {
      console.warn(`❌ Failed to parse localStorage key "${key}":`, error);
    }
    return defaultValue;
  });

  const lastSaved = useRef(state);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (skipSave) return;
    if (JSON.stringify(state) === JSON.stringify(lastSaved.current)) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify(state));
        lastSaved.current = state;
      } catch (error) {
        console.warn(`❌ Failed to save "${key}" in localStorage:`, error);
      }
    }, 100);
  }, [state, key, skipSave]);

  useEffect(() => () => clearTimeout(saveTimeoutRef.current), []);

  return [state, setState];
};

//* -------------------- Provider principale --------------------
export const CasaDiVetroProvider = ({ children }) => {
  const urlParams = useUrlParams();
  const [skipSave, setSkipSave] = useState(false);

  const initialState = useMemo(
    () => ({
      filters: { ...defaultFilters },
      pagination: { ...defaultPagination },
      uuid: urlParams.uuid,
    }),
    [urlParams.uuid],
  );

  const [state, setState] = usePersistedState(
    STORAGE_KEY,
    initialState,
    skipSave,
  );

  //* Reset automatico quando cambia l’UUID
  useEffect(() => {
    if (urlParams.uuid !== state.uuid) {
      setSkipSave(true);

      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        console.warn("⚠️ Errore nella rimozione localStorage:", e);
      }

      setState({
        filters: { ...defaultFilters },
        pagination: { ...defaultPagination },
        uuid: urlParams.uuid,
      });

      const t = setTimeout(() => setSkipSave(false), 300);
      return () => clearTimeout(t);
    }
  }, [urlParams.uuid, state.uuid, setState]);

  //* -------------------- Metodi helper --------------------
  const setFilterCDV = useCallback(
    (name, value) => {
      setState((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          [name]: value,
        },
      }));
    },
    [setState],
  );

  const setFiltersCDV = useCallback(
    (newFilters) => {
      setState((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          ...newFilters,
        },
      }));
    },
    [setState],
  );

  const resetFiltersCDV = useCallback(() => {
    setState((prev) => ({
      ...prev,
      filters: { ...defaultFilters },
    }));
  }, [setState]);

  const setPagination = useCallback(
    (newPagination) => {
      setState((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          ...newPagination,
        },
      }));
    },
    [setState],
  );

  const setPage = useCallback(
    (page) => {
      setPagination({ page });
    },
    [setPagination],
  );

  const setSize = useCallback(
    (size) => {
      setPagination({ size, page: 0 });
    },
    [setPagination],
  );

  const setSortCdv = useCallback(
    (sort) => {
      setPagination({ sort });
    },
    [setPagination],
  );

  const resetAll = useCallback(() => {
    setState({
      filters: { ...defaultFilters },
      pagination: { ...defaultPagination },
      uuid: urlParams.uuid,
    });
  }, [setState, urlParams.uuid]);

  /* -------------------- Utilità per query string -------------------- */
  const getQueryParams = useCallback(() => {
    const params = {};

    Object.entries(state.filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        params[key] = value;
      }
    });

    params.page = state.pagination.page;
    params.size = state.pagination.size;
    if (state.pagination.sort) params.sort = state.pagination.sort;

    return params;
  }, [state]);

  const buildQueryString = useCallback(() => {
    const params = getQueryParams();
    const queryString = Object.entries(params)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
      )
      .join("&");
    return queryString ? `?${queryString}` : "";
  }, [getQueryParams]);

  const hasActiveFilters = useCallback(() => {
    return Object.values(state.filters).some(
      (value) => value !== null && value !== undefined && value !== "",
    );
  }, [state.filters]);

  //* -------------------- Memo del valore di contesto --------------------
  const value = useMemo(
    () => ({
      filtersCDV: state.filters,
      pagination: state.pagination,
      setFilterCDV,
      setFiltersCDV,
      resetFiltersCDV,
      setPagination,
      setPage,
      setSize,
      setSortCdv,
      resetAll,
      getQueryParams,
      buildQueryString,
      hasActiveFilters,
    }),
    [
      state,
      setFilterCDV,
      setFiltersCDV,
      resetFiltersCDV,
      setPagination,
      setPage,
      setSize,
      setSortCdv,
      resetAll,
      getQueryParams,
      buildQueryString,
      hasActiveFilters,
    ],
  );

  return (
    <CasaDiVetroContext.Provider value={value}>
      {children}
    </CasaDiVetroContext.Provider>
  );
};

//* -------------------- Hook pubblico --------------------
export const useCasaDiVetroFilter = () => {
  const context = useContext(CasaDiVetroContext);
  if (!context) {
    throw new Error(
      "useCasaDiVetroFilter deve essere usato dentro CasaDiVetroProvider",
    );
  }
  return context;
};

export default CasaDiVetroContext;
